import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MatDrawer} from '@angular/material/sidenav';
import {Observable} from 'rxjs';
import {ErrorCodes} from '../../../constants/ErrorCodes';
import {UserFunctionCode} from '../../../constants/UserFunctionCode';
import {FileUploadService} from '../../../services/file-upload.service';
import {NotificationService} from '../../../services/notification.service';
import {SecurityService} from '../../../services/security.service';

/**
 * This component allows the user to upload a file to the server.
 * The component has a drawer that opens when the user clicks on the button.
 * The drawer contains a file input and an upload button.
 *
 * The component has the following inputs:
 * - endpoint: The endpoint to which the file will be uploaded. Required.
 * - showSuccessResponseNotifications: Whether to show notifications on success responses. Default is true. This should only be set to false if the component is used in a parent component that handles the response.
 * - showFailureResponseNotifications: Whether to show error notifications on failure responses. Default is true. This should only be set to false if the component is used in a parent component that handles the response.
 *
 * The component emits two events:
 * - fileUploadSuccessEvent: This event is emitted when the file upload is successful. The response is passed as an argument.
 * - fileUploadFailureEvent: This event is emitted when the file upload fails. The error is passed as an argument.
 *
 * @author Cameron Holly (GZ3VKV)
 */
@Component({
  selector: 'rem-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent implements OnInit {

  /**
   * The endpoint to which the file will be uploaded. Required.
   */
  @Input() endpoint: string;

  /**
   * Whether to show notifications on success responses. Default is true.
   * This should only be set to false if the component is used in a parent component that handles the response.
   */
  @Input() showSuccessResponseNotifications: boolean = true;

  /**
   * Whether to show error notifications on failure responses. Default is true.
   * This should only be set to false if the component is used in a parent component that handles the response.
   */
  @Input() showFailureResponseNotifications: boolean = true;

  /**
   * This event is emitted when the file upload is successful. The response is passed as an argument.
   */
  @Output() fileUploadSuccessEvent = new EventEmitter<any>();

  /**
   * This event is emitted when the file upload fails. The error is passed as an argument.
   */
  @Output() fileUploadFailureEvent = new EventEmitter<any>();

  @ViewChild('drawer') matDrawer: MatDrawer;
  @ViewChild('fileInput') fileInput: ElementRef;

  public isLoading: boolean = false;

  public file: File | null;

  allowedExtensions: string[] = [];

  fileSizeLimit: number = 0;

  hasSpecError: boolean = false;

  public get isFileSelected(): boolean {
    return this.file != null;
  }

  public get acceptsExtensions(): string {
    return this.allowedExtensions.map(e => '.' + e).join(', ');
  }

  public canUpload: boolean = false;

  constructor(private readonly fileUploadService: FileUploadService,
              private readonly notificationService: NotificationService,
              private readonly securityService: SecurityService) {
  }

  ngOnInit(): void {
    this.canUpload = this.securityService.can(UserFunctionCode.VEHICLE_ROLES);
    this.getSpec();
  }

  selectFile(event: any) {
    const file: File = event.target.files[0];
    if (this.exceedsFileSize(file)) {
      this.notificationService.open(
        ErrorCodes.INVALID_FILE_SIZE_X.replace('{x}', this.formatFileSize(file.size)), 'error');
      this.resetFileSelection();
      return;
    }
    if (!this.isAllowedFileExtension(file)) {
      this.notificationService.open(
        ErrorCodes.INVALID_FILE_EXTENSION_LIST.replace('{x}', this.allowedExtensions.join(', ')), 'error');
      this.resetFileSelection();
      return;
    }
    this.file = file;
  }

  uploadFile() {
    if (this.file && this.endpoint) {
      this.isLoading = true;
      const obs = this.getObservable(this.file);
      this.executeObservable(obs);
    }
  }

  getObservable(file: File): Observable<any> {
    return this.fileUploadService.upload(file, this.endpoint);
  }

  executeObservable(obs: Observable<any>) {
    obs.subscribe({
      next: (response) => {
        if (this.showSuccessResponseNotifications) {
          this.notificationService.open(ErrorCodes.FILE_UPLOAD_SUCCESS, 'success');
        }
        this.resetAll();
        this.fileUploadSuccessEvent.emit(response);
      },
      error: (error) => {
        if (this.showFailureResponseNotifications) {
          this.notificationService.open(this.notificationService.getErrorByCode(error.error.errorCode), 'error');
        }
        this.resetAll();
        this.fileUploadFailureEvent.emit(error);
      }
    });
  }

  resetAll() {
    this.isLoading = false;
    this.matDrawer.close();
    this.resetFileSelection();
  }

  resetFileSelection() {
    this.file = null;
    this.fileInput.nativeElement.value = null;
  }

  isAllowedFileExtension(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase();
    return extension ? this.allowedExtensions.includes(extension) : false;
  }

  exceedsFileSize(file: File): boolean {
    return file.size > this.fileSizeLimit;
  }

  private getSpec() {
    this.isLoading = true;
    this.fileUploadService.getSpec(this.endpoint).subscribe({
      next: (response) => {
        this.allowedExtensions = response.allowedExtensions;
        this.fileSizeLimit = response.fileSizeLimit;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.hasSpecError = true;
      }
    });
  }

  /**
   * Returns the size of the file in the most appropriate units. Works below 1TB.
   * @param size
   */
  formatFileSize(size: number): string {
    const k = size > 0 ? Math.floor((Math.log2(size) / 10)) : 0;
    const unit = (k > 0 ? 'KMG'[k - 1] : '') + 'B';
    const count = Math.floor(size / Math.pow(1024, k));
    return count + unit;
  }
}
